import { ComponentType, lazy } from 'react';

type ComponentImport<T> = () => Promise<{ default: T }>;

interface LazyWithRetryOptions {
  retries?: number;
  interval?: number;
}

/**
 * 네트워크 오류 시 자동 재시도
 *
 * @param componentImport - 동적 import 함수
 * @param options - 재시도 옵션
 */
export function lazyWithRetry<T extends ComponentType<any>>(
  componentImport: ComponentImport<T>,
  options: LazyWithRetryOptions = {},
): React.LazyExoticComponent<T> {
  const { retries = 3, interval = 1500 } = options;

  return lazy(async () => {
    let lastError: Error | undefined;

    for (let attempt = 0; attempt < retries; attempt++) {
      try {
        // 첫 시도가 아니면 캐시 버스팅을 위한 지연
        if (attempt > 0) await sleep(interval);

        return await componentImport();
      } catch (error) {
        lastError = error as Error;

        // ChunkLoadError가 아닌 경우 즉시 throw
        if (!isChunkLoadError(lastError)) throw lastError;

        console.warn(
          `[lazyWithRetry] Chunk load failed, attempt ${attempt + 1}/${retries}`,
          lastError.message,
        );
      }
    }

    // 모든 재시도 실패
    throw lastError ?? new Error('Failed to load component after retries');
  });
}

function isChunkLoadError(error: Error): boolean {
  return (
    error.name === 'ChunkLoadError' ||
    error.message.includes('Loading chunk') ||
    error.message.includes('Failed to fetch dynamically imported module') ||
    error.message.includes('Loading CSS chunk')
  );
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export default lazyWithRetry;
